// VitaMD two-AI PAIRS — the /vitamd landing picker.
//
// This is the dual-voice analogue of personas.js (the single-voice men's-health
// page). personas.js is left UNTOUCHED; pairs live here so the two pages can
// evolve independently.
//
// Each card surfaces a duo: a friendly HOST + a credible EXPERT. The `id` is the
// STABLE, BACKEND-FACING pair identifier (medical | executive | young) — it is
// sent to the server as `pair_id` and (Phase 4) selects which duo runs the call.
//
// Voice IDs (host / expert) come from the project_voice_call_engagement memory
// and are used by the dual-voice generator (app/scripts/generate-pair-audio.mjs)
// — the frontend never calls ElevenLabs directly; it only plays the pre-rendered
// stitched MP3s at /personas/audio/{pairId}/NN.mp3.
import { Stethoscope, Briefcase, Sparkles } from 'lucide-react';

// 6 stitched host↔expert teaser clips per pair, played from the card play-button.
// First click is the warm intro (AI disclosed); subsequent clicks cycle through
// progressively nudgier prompts toward leaving a number. Source-of-truth scripts
// live at app/scripts/persona-audio-scripts.json (the `pairs` block); regenerate
// with: ELEVENLABS_API_KEY=… node app/scripts/generate-pair-audio.mjs --force
const AUDIO_CLIP_COUNT = 6;
function audioClips(pairId) {
  return Array.from({ length: AUDIO_CLIP_COUNT }, (_, i) =>
    `/personas/audio/pairs/${pairId}/${String(i + 1).padStart(2, '0')}.mp3`
  );
}

// `name`/`displayName` are ordered LEFT→RIGHT to match the people in each card
// image. `tagline`/`blurb`/`cta` are marketing copy ({en,es}); VitamdStart passes
// the language-resolved strings to PersonaCard (bypassing the men's-health i18n
// persona keys). `host`/`expert` keep their roles (host=order-0 voice on the call),
// independent of the display order.
export const PAIR_PERSONAS = [
  {
    id: 'medical',
    name: 'Doctor Sanchez & Barbara',
    displayName: 'Doctor Sanchez & Barbara',
    tagline: { en: 'Real answers, warmly explained.', es: 'Respuestas reales, explicadas con calidez.' },
    blurb: {
      en: 'A straight-talking doctor and a friendly guide — clear answers about losing weight, zero pressure.',
      es: 'Un doctor directo y una guía amable — respuestas claras sobre bajar de peso, sin presión.'
    },
    cta: { en: 'Talk it through', es: 'Hablémoslo' },
    image: '/personas/pair-medical.png',
    icon: Stethoscope,
    accent: 'from-teal-800/55 to-stone-950',
    ring: 'ring-teal-400',
    host: { name: 'Barbara', voiceId: 'EXAVITQu4vr4xnSDxMaL' },
    expert: { name: 'Doctor Sanchez', voiceId: 'HRttR5MBBbw7AvtIFoRq' },
    audioClips: audioClips('medical')
  },
  {
    id: 'executive',
    name: 'Thomas & Sonya',
    displayName: 'Thomas & Sonya',
    tagline: { en: 'Sharp. Respects your time.', es: 'Directo. Respeta tu tiempo.' },
    blurb: {
      en: 'No fluff — the facts on weight loss and what fits a packed schedule.',
      es: 'Sin rodeos — los hechos sobre bajar de peso y lo que cabe en una agenda ocupada.'
    },
    cta: { en: 'Give me the rundown', es: 'Dame el resumen' },
    image: '/personas/pair-executive.png',
    icon: Briefcase,
    accent: 'from-slate-700/60 to-stone-950',
    ring: 'ring-slate-400',
    host: { name: 'Sonya', voiceId: '86U1Fs5aPKKkDKi9PRxz' },
    expert: { name: 'Thomas', voiceId: '1YBpxMFAafA83t7u1xof' },
    audioClips: audioClips('executive')
  },
  {
    id: 'young',
    name: 'Josh & Cowboy Jay',
    displayName: 'Josh & Cowboy Jay',
    tagline: { en: 'Real talk. No pressure.', es: 'Sin rodeos. Sin presión.' },
    blurb: {
      en: 'Laid-back guys who break down weight loss without the jargon.',
      es: 'Tipos relajados que explican bajar de peso sin tecnicismos.'
    },
    cta: { en: "Let's chat", es: 'Conversemos' },
    image: '/personas/pair-young.png',
    icon: Sparkles,
    accent: 'from-amber-700/50 to-stone-950',
    ring: 'ring-amber-500',
    host: { name: 'Cowboy Jay', voiceId: '4m3xt3xfssayzO1e9shv' },
    expert: { name: 'Josh', voiceId: 'GoGUcAZovo4MFeLxJdZd' },
    audioClips: audioClips('young')
  }
];

export function findPair(id) {
  return PAIR_PERSONAS.find((p) => p.id === id) || null;
}
